import { Component, OnInit, ViewChild } from '@angular/core';
import { User } from '../../../models/User/user';
import { AppContextService } from '../../../services/app-context.service';
import { OperationResult } from '../../../models/server-result/operation-result';
import { ConfirmingContract } from '../../../models/contracts/confirming/confirming-contract';
import { ReportingApiService } from '../../../server-api/reporting-api/reporting-api.service';
import { ServerApiService } from '../../../server-api/server-api.service';
import { ToasterService } from '../../../shared-components/custom-toaster/toaster.service';
import { HttpErrorResponse } from '@angular/common/http';
import { DateLocalePipe } from '../../../services/date-locale.pipe';
import { ConfirmingConnectionsSearchCriteria } from '../../../models/contracts/confirming/confirming-connections-search-criteria';
import { ConfirmingConnection } from '../../../models/contracts/confirming/confirming-connection';
import { forkJoin, Observable } from 'rxjs';
import { OperationError } from '../../../models/server-result/operation-error';
import { GetFinancierConfirmingContractsInput } from '../../../models/contracts/confirming/get-financier-confirming-contracts';
import { CurrencyCode } from '../../../models/enums/currency-code';
import { Router } from '@angular/router';
import { FileIntegrationStateService } from '../../../services/file-integration-state.service';
import { SearchFilesIntegrationComponent } from '../../files-integration/search-files-integration/search-files-integration.component';
import { FileStatus } from '../../../models/enums/FileIntegration/integration-file-status';
import { TranslateService } from '../../../services/translate/translate.service';
import { CurrencyViewModel, DASHBOARD_TAB_CONFIRMING, DASHBOARD_TAB_FACTORING } from '../dashboard.types';
import { DashboardHelperService } from '../dashboard-helper.service';
import { DashboardFactoringTabComponent } from '../dashboard-factoring-tab/dashboard-factoring-tab.component';
import { CustomTabItem, TabAction } from '../../../shared-components/custom-tabs/custom-tabs.component';
import { SegmentedControlOption } from '../../../shared-components/custom-segmented-control/custom-segmented-control.component';

@Component({
	selector: 'app-dashboard-financier',
	templateUrl: './dashboard-financier.component.html',
	styleUrls: ['../private-home.component.scss', './dashboard-financier.component.scss'],
	standalone: false,
})
export class DashboardFinancierComponent implements OnInit {
	public loggedUser: User | null = new User();
	public requestErrors: OperationError[] = [];

	public CurrentMonth: Date;

	public registeredBuyerCompanies: number = 0;
	public contractsAvailableAmount: number = 0;
	public contractedAmount: number = 0;
	public approvedRequestFunds: number = 0;
	public contractsAvailableAmountRatio: number = 0;
	public buyersWithActiveContractsCompaniesCount: number = 0;
	public suppliersWithActiveConnectionsCompaniesCount: number = 0;
	public suppliersActiveConnections: number = 0;
	public monthlyAutomaticPayments: number = 0;
	public currentFundingRequestsAmount: number = 0;
	public notPaidDocuments: number = 0;

	private month: number = new Date().getMonth() + 1;
	private year: number = new Date().getFullYear();

	public selectedCurrency: string;
	public orderedCurrencies: CurrencyViewModel[] = [];
	public currencyOptions: SegmentedControlOption[] = [];

	public confirmingTabVisible: boolean = false;
	public factoringTabVisible: boolean = false;
	public activeTabId: string = '';
	public tabs: CustomTabItem[] = [];
	public tabActions: TabAction[] = [];

	protected DASHBOARD_TAB_CONFIRMING = DASHBOARD_TAB_CONFIRMING;
	protected DASHBOARD_TAB_FACTORING = DASHBOARD_TAB_FACTORING;

	public confirmingPendingApprovalDocumentsAmount: number = 0;

	protected CurrencyCode = CurrencyCode;
	protected FileStatus = FileStatus;

	// Loading states for dashboard cards
	public loadingContractedAmount: boolean = false;
	public loadingContractsAvailableAmount: boolean = false;
	public loadingNotPaidDocuments: boolean = false;
	public loadingApprovedRequestFunds: boolean = false;
	public loadingCurrentFundingRequestsAmount: boolean = false;
	public loadingMonthlyAutomaticPayments: boolean = false;
	public loadingBuyersWithActiveContractsCompaniesCount: boolean = false;
	public loadingSuppliersWithActiveConnectionsCompaniesCount: boolean = false;
	public loadingSuppliersActiveConnections: boolean = false;
	public loadingConfirmingPendingApprovalDocumentsAmount: boolean = false;

	constructor(
		private reportingApiService: ReportingApiService,
		private serverApiService: ServerApiService,
		private toasterService: ToasterService,
		private appContext: AppContextService,
		private dateLocale: DateLocalePipe,
		private router: Router,
		private fileIntegrationStateService: FileIntegrationStateService,
		private translateService: TranslateService,
		private dashboardHelper: DashboardHelperService
	) {}

	ngOnInit() {
		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;

			this.CurrentMonth = this.dateLocale.transform(new Date(), 'MM/yyyy');

			if (this.loggedUser?.canSeeConfirmingOptions()) {
				this.confirmingTabVisible = true;
				this.clickedConfirmingTab();
			}

			if (this.loggedUser?.canSeeFactoringOptions()) {
				this.factoringTabVisible = true;

				if (this.activeTabId !== DASHBOARD_TAB_CONFIRMING) this.clickedFactoringTab();
			}

			this.tabs = this.dashboardHelper.createTabs(this.confirmingTabVisible, this.factoringTabVisible);
			this.tabActions = [this.dashboardHelper.createRefreshAction(() => this.refreshAllData())];
		});
	}

	@ViewChild(DashboardFactoringTabComponent) private factoringTab: DashboardFactoringTabComponent | undefined;

	public refreshAllData(): void {
		if (this.activeTabId === DASHBOARD_TAB_CONFIRMING) {
			this.getContracts();
		} else if (this.activeTabId === DASHBOARD_TAB_FACTORING) {
			this.factoringTab?.refresh();
		}
	}

	private getCurrentFundingRequestsAmount(currency: string): void {
		this.currentFundingRequestsAmount = 0;
		this.dashboardHelper.loadMetric(
			this.reportingApiService.getConfirmingCurrentFundingRequestsAmount(currency),
			(loading) => (this.loadingCurrentFundingRequestsAmount = loading),
			(value) => (this.currentFundingRequestsAmount = value),
			'[DashboardFinancierComponent] Invalid result from getCurrentFundingRequestsAmount()'
		);
	}

	private getMonthlyAutomaticPayments(currency: string): void {
		this.monthlyAutomaticPayments = 0;
		this.dashboardHelper.loadMetric(
			this.reportingApiService.getConfirmingMonthlyAutomaticPayments(this.month, this.year, currency),
			(loading) => (this.loadingMonthlyAutomaticPayments = loading),
			(value) => (this.monthlyAutomaticPayments = value),
			'[DashboardFinancierComponent] Invalid result from getMonthlyAutomaticPayments()'
		);
	}

	private getSuppliersActiveConnections(currency: string): void {
		this.suppliersActiveConnections = 0;
		this.dashboardHelper.loadMetric(
			this.reportingApiService.getConfirmingSuppliersActiveConnections(currency),
			(loading) => (this.loadingSuppliersActiveConnections = loading),
			(value) => (this.suppliersActiveConnections = value),
			'[DashboardFinancierComponent] Invalid result from getSuppliersActiveConnections()'
		);
	}

	private getPendingApprovalDocumentsAmount(currency: string) {
		this.confirmingPendingApprovalDocumentsAmount = 0;
		this.dashboardHelper.loadMetric(
			this.reportingApiService.getPendingApprovalDocumentsAmount(currency),
			(loading) => (this.loadingConfirmingPendingApprovalDocumentsAmount = loading),
			(value) => (this.confirmingPendingApprovalDocumentsAmount = value.Confirming),
			'[DashboardAdminitratorComponent] Invalid result from getPendingApprovalDocumentsAmount',
			'[DashboardAdminitratorComponent] HttpError result from getPendingApprovalDocumentsAmount'
		);
	}

	private getContracts() {
		// Set loading states for values that are calculated in this method
		this.loadingContractedAmount = true;
		this.loadingContractsAvailableAmount = true;
		this.loadingBuyersWithActiveContractsCompaniesCount = true;

		this.resetValues();

		let servicesCallsToMake: Array<Observable<OperationResult<ConfirmingContract[]>>> = [];

		this.loggedUser?.Companies.forEach((company) => {
			servicesCallsToMake.push(
				this.serverApiService.getConfirmingContractsForFinancier(<GetFinancierConfirmingContractsInput>{
					FinancierId: company.Id!,
				})
			);
		});

		const getAllRequests = forkJoin(servicesCallsToMake);

		getAllRequests.subscribe((opResults) => {
			let currencies: CurrencyViewModel[] = [];
			let retrievedConfirmingContracts: ConfirmingContract[] = [];

			for (let index = 0; index < servicesCallsToMake.length; index++) {
				let getConfirmingContracts = opResults[index] as OperationResult<ConfirmingContract[]>;

				if (!getConfirmingContracts.IsValid) {
					let errors = getConfirmingContracts.Errors;
					if (errors && errors.length > 0) {
						this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
							errors: errors,
							autoClose: false,
						});
					}
				} else {
					if (!!getConfirmingContracts.Data && getConfirmingContracts.Data.length > 0) {
						this.registeredBuyerCompanies += getConfirmingContracts.Data.length;

						getConfirmingContracts.Data.forEach((contract) => {
							retrievedConfirmingContracts.push(contract);
							this.dashboardHelper.addCurrencyIfMissing(currencies, contract.Currency);
						});
					}
				}
			}

			this.orderedCurrencies = this.dashboardHelper.sortCurrencies(currencies);
			this.currencyOptions = this.dashboardHelper.toCurrencyOptions(this.orderedCurrencies);

			if (!!this.orderedCurrencies && this.orderedCurrencies.length > 0) {
				let initialCurrencytoProcess = this.orderedCurrencies[0].Currency;

				let contractsOfInitialCurrency: ConfirmingContract[] = retrievedConfirmingContracts.filter(
					(c) => c.Currency == initialCurrencytoProcess
				);

				contractsOfInitialCurrency.forEach((contract) => {
					this.contractedAmount += contract.ContractedAmount;
					this.contractsAvailableAmount += contract.AvailableAmount;
					this.contractsAvailableAmountRatio = this.dashboardHelper.contractsAvailableRatio(
						this.contractsAvailableAmount,
						this.contractedAmount
					);
				});

				this.buyersWithActiveContractsCompaniesCount = this.dashboardHelper.distinctCount(
					contractsOfInitialCurrency.filter((contract) => contract.ContractState == 'Active').map((c) => c.Buyer.Id)
				);

				this.selectedCurrency = initialCurrencytoProcess;

				this.getApprovedRequestFunds(initialCurrencytoProcess);
				this.getActiveConnections(initialCurrencytoProcess);
				this.getSuppliersActiveConnections(initialCurrencytoProcess);
				this.getMonthlyAutomaticPayments(initialCurrencytoProcess);
				this.getCurrentFundingRequestsAmount(initialCurrencytoProcess);
				this.getNotPaidDocumentsSum(initialCurrencytoProcess);
				this.getPendingApprovalDocumentsAmount(initialCurrencytoProcess);
			}

			// Set loading states to false after all calculations are complete
			this.loadingContractedAmount = false;
			this.loadingContractsAvailableAmount = false;
			this.loadingBuyersWithActiveContractsCompaniesCount = false;
		});
	}

	private refreshContractDataForCurrency(currency: string) {
		// Set loading states for values that are calculated in this method
		this.loadingContractedAmount = true;
		this.loadingContractsAvailableAmount = true;
		this.loadingBuyersWithActiveContractsCompaniesCount = true;

		let servicesCallsToMake: Array<Observable<OperationResult<ConfirmingContract[]>>> = [];

		this.loggedUser?.Companies.forEach((company) => {
			servicesCallsToMake.push(
				this.serverApiService.getConfirmingContractsForFinancier(<GetFinancierConfirmingContractsInput>{
					FinancierId: company.Id!,
					Currency: currency,
				})
			);
		});

		const getAllRequests = forkJoin(servicesCallsToMake);

		getAllRequests.subscribe((opResults) => {
			for (let index = 0; index < servicesCallsToMake.length; index++) {
				let getConfirmingContracts = opResults[index] as OperationResult<ConfirmingContract[]>;

				if (!getConfirmingContracts.IsValid) {
					let errors = getConfirmingContracts.Errors;
					if (errors && errors.length > 0) {
						this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
							errors: errors,
							autoClose: false,
						});
					}
				} else {
					if (!!getConfirmingContracts.Data && getConfirmingContracts.Data.length > 0) {
						this.registeredBuyerCompanies += getConfirmingContracts.Data.length;

						getConfirmingContracts.Data.forEach((contract) => {
							this.contractedAmount += contract.ContractedAmount;
							this.contractsAvailableAmount += contract.AvailableAmount;
							this.contractsAvailableAmountRatio = this.dashboardHelper.contractsAvailableRatio(
								this.contractsAvailableAmount,
								this.contractedAmount
							);
						});

						this.buyersWithActiveContractsCompaniesCount = this.dashboardHelper.distinctCount(
							getConfirmingContracts.Data.filter((contract) => contract.ContractState == 'Active').map((c) => c.Buyer.Id)
						);
					}
				}
			}

			// Set loading states to false after all calculations are complete
			this.loadingContractedAmount = false;
			this.loadingContractsAvailableAmount = false;
			this.loadingBuyersWithActiveContractsCompaniesCount = false;
		});
	}

	private getApprovedRequestFunds(currency: string) {
		this.approvedRequestFunds = 0;
		const fundingRequestDocumentStates = ['Approved'];

		this.dashboardHelper.loadMetric(
			this.reportingApiService.getConfirmingFundingRequestsTotalAdvancedAmount(fundingRequestDocumentStates, currency),
			(loading) => (this.loadingApprovedRequestFunds = loading),
			(value) => (this.approvedRequestFunds = value),
			'[DashboardFinancierComponent] Invalid result from getFundingRequestsTotalAdvancedAmount',
			'[DashboardFinancierComponent] HttpError result from getFundingRequestsTotalAdvancedAmount'
		);
	}

	private getActiveConnections(currency: string) {
		this.loadingSuppliersWithActiveConnectionsCompaniesCount = true;
		this.suppliersWithActiveConnectionsCompaniesCount = 0;
		const criteria: ConfirmingConnectionsSearchCriteria = new ConfirmingConnectionsSearchCriteria();
		criteria.PageNumber = 1;
		criteria.RowsPerPage = 2147483647;
		criteria.RestrictionCriteria = new ConfirmingConnectionsSearchCriteria();
		criteria.RestrictionCriteria.ConnectionStateList = ['Active'];
		criteria.RestrictionCriteria.ContractStateList = ['Active'];
		criteria.RestrictionCriteria.Currency = currency;

		this.serverApiService.searchConfirmingConnections(criteria).subscribe({
			next: (operationResult: OperationResult<ConfirmingConnection[]>) => {
				this.loadingSuppliersWithActiveConnectionsCompaniesCount = false;
				if (operationResult.IsValid) {
					if (!!operationResult.Data) {
						if (operationResult.Data.length > 0)
							this.suppliersWithActiveConnectionsCompaniesCount = this.dashboardHelper.distinctCount(
								operationResult.Data.map((c) => c.Supplier.Id)
							);
						else this.suppliersWithActiveConnectionsCompaniesCount = 0;
					}
				} else {
					this.toasterService.showError(this.translateService.translate('ERROR_GET_COMPANY_CONTRACTS', 'list-errorCodes'));
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingSuppliersWithActiveConnectionsCompaniesCount = false;
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
			},
		});
	}

	public clickedCurrency(currency: string): void {
		this.resetValues();

		this.selectedCurrency = currency;

		this.refreshContractDataForCurrency(currency);
		this.getApprovedRequestFunds(currency);
		this.getActiveConnections(currency);
		this.getSuppliersActiveConnections(currency);
		this.getMonthlyAutomaticPayments(currency);
		this.getCurrentFundingRequestsAmount(currency);
		this.getNotPaidDocumentsSum(currency);
		this.getPendingApprovalDocumentsAmount(currency);
	}

	private resetValues() {
		this.contractedAmount = 0;
		this.contractsAvailableAmount = 0;
		this.contractsAvailableAmountRatio = 0;
		this.approvedRequestFunds = 0;
		this.currentFundingRequestsAmount = 0;
		this.monthlyAutomaticPayments = 0;
		this.buyersWithActiveContractsCompaniesCount = 0;
		this.suppliersWithActiveConnectionsCompaniesCount = 0;
		this.suppliersActiveConnections = 0;
		this.notPaidDocuments = 0;
	}

	public clickedConfirmingTab(): void {
		this.activeTabId = DASHBOARD_TAB_CONFIRMING;

		this.getContracts();
	}

	private getNotPaidDocumentsSum(currency: string): void {
		this.notPaidDocuments = 0;
		this.dashboardHelper.loadMetric(
			this.reportingApiService.getNotPaidDocumentsSum(currency),
			(loading) => (this.loadingNotPaidDocuments = loading),
			(value) => (this.notPaidDocuments = value),
			'[DashboardFinancierComponent] Invalid result from getNotPaidDocumentsSum()',
			'[DashboardFinacierComponent] Invalid result from getNotPaidDocumentsSum()'
		);
	}

	public clickedFactoringTab(): void {
		this.activeTabId = DASHBOARD_TAB_FACTORING;
		this.selectedCurrency = CurrencyCode.Euro;
	}

	public onTabChange(tabId: string): void {
		if (tabId === DASHBOARD_TAB_CONFIRMING) this.clickedConfirmingTab();
		else if (tabId === DASHBOARD_TAB_FACTORING) this.clickedFactoringTab();
	}

	public navigateToFilesIntegration(key: string): void {
		if (!this.loggedUser?.IsFinancier) {
			SearchFilesIntegrationComponent.navigateWithFileStatus(this.router, this.fileIntegrationStateService, key);
		}
	}
}
